import React, { FC } from 'react';
import { Box, Typography, Link, Button, CircularProgress } from '@mui/material';
import { MainTitle, TextField } from 'components/generic';
import { NavLink } from 'react-router-dom';
import { useLoginInputStep } from 'hooks/auth';

interface LoginInputStepProps {
  onClose: () => void;
  nextStep: () => void;
}

const LoginInputStep: FC<LoginInputStepProps> = ({ onClose, nextStep }) => {
  const {
    t,
    control,
    title,
    text,
    inputLabel,
    pattern,
    mask,
    maskPlaceholder,
    codeIsLoading,
    inputType,
  } = useLoginInputStep(nextStep);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '368px',
      }}
    >
      <Box>
        <Box sx={{ marginBottom: '12px' }}>
          <MainTitle>{title}</MainTitle>
        </Box>
        <Typography
          sx={{
            marginBottom: '45px',
            fontSize: '18px',
            lineHeight: '21px',
          }}
        >
          {text}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <TextField
          control={control}
          name="login"
          label={inputLabel}
          pattern={pattern}
          mask={mask}
          maskPlaceholder={maskPlaceholder}
          type={inputType}
          required
          endIcon={
            codeIsLoading ? (
              <CircularProgress size={25} color="primary" />
            ) : (
              <></>
            )
          }
        />
      </Box>

      <Typography
        sx={{
          marginBottom: '20px',
          fontSize: '14px',
          lineHeight: '18px',
        }}
        variant="body2"
      >
        {t('login.agreement.text_1')}{' '}
        <Link to="/terms_of_use" target="_blank" component={NavLink}>
          {t('login.agreement.link_1')}
        </Link>{' '}
        {t('login.agreement.text_2')}{' '}
        <Link to="/privacy_policy" target="_blank" component={NavLink}>
          {t('login.agreement.link_2')}
        </Link>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
        gap={2}
      >
        <Button
          sx={{
            padding: '10px 0',
            textTransform: 'inherit',
            fontSize: '18px',
            lineHeight: '21px',
            borderRadius: '6px',
            fontWeight: '300',
            color: 'primary.main',
            boxShadow: 'none',
          }}
          variant="text"
          color="inherit"
          onClick={onClose}
          fullWidth
        >
          {t('login.later')}
        </Button>
        <Button
          sx={{
            padding: '15px 0',
            textTransform: 'inherit',
            fontSize: '18px',
            lineHeight: '21px',
            borderRadius: '6px',
            fontWeight: '300',
            color: 'primary.contrastText',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:focus': {
              boxShadow: 'none',
            },
          }}
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
        >
          {t('continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginInputStep;
